// Makes everything outside an open modal popup `inert`, not just `aria-hidden`.
//
// Radix hides the rest of the page from assistive tech (`aria-hidden`) while a modal menu or a
// select is open, and blocks outside pointer events, but the hidden controls stay focusable.
// axe flags that as `aria-hidden-focus` (serious): a screen-reader user could tab into
// something they are told isn't there. Dialogs escape the rule because they carry
// `aria-modal`; menus and listboxes can't. `inert` removes the hidden part of the page from
// focus as well, so what is hidden is also unreachable. It applies to the same elements Radix
// hides (the same walk as the `aria-hidden` package's hideOthers: everything outside the popup
// and outside live regions) and is undone when the popup closes.
//
// Not `inertOthers` from `aria-hidden`: that package counts hide/inert calls per node in one
// shared map, so stacking its inert on Radix's aria-hidden leaves `inert` behind after close.

import { useEffect, useRef } from 'react';

/** How many open popups made each element inert (popups can stack: a select in a dialog). */
const inertCount = new WeakMap<Element, number>();

/**
 * Makes everything in `document.body` inert except `target`, live regions (`[aria-live]`,
 * so announcements such as toasts keep working) and their ancestors. Returns the undo.
 */
export function inertOutside(target: Element): () => void {
  const body = target.ownerDocument.body;
  const keep = new Set<Node>();
  const keepPath = (from: Node) => {
    for (let n: Node | null = from; n && !keep.has(n); n = n.parentNode) keep.add(n);
  };
  const stop = new Set<Element>([target]);
  keepPath(target);
  for (const live of body.querySelectorAll('[aria-live]')) {
    keepPath(live);
    stop.add(live);
  }

  const marked: Element[] = [];
  const walk = (parent: Element) => {
    if (stop.has(parent)) return;
    for (const child of Array.from(parent.children)) {
      if (keep.has(child)) {
        walk(child);
        continue;
      }
      if (child.tagName === 'SCRIPT') continue;
      const count = inertCount.get(child) ?? 0;
      if (count === 0 && child.hasAttribute('inert')) continue; // inert already, not ours
      inertCount.set(child, count + 1);
      if (count === 0) child.setAttribute('inert', '');
      marked.push(child);
    }
  };
  walk(body);

  return () => {
    for (const el of marked) {
      const count = (inertCount.get(el) ?? 1) - 1;
      if (count > 0) {
        inertCount.set(el, count);
      } else {
        inertCount.delete(el);
        el.removeAttribute('inert');
      }
    }
    marked.length = 0;
  };
}

/**
 * Rendered as a child of a modal popup's content (menu, select list). While it is mounted in
 * the document, everything outside the popup is inert. A child component, not a ref on the
 * content: its effect lives exactly as long as the open popup, and Radix renders a closed
 * select's items into a detached fragment, where this does nothing.
 */
export function InertOutside() {
  const marker = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const popup = marker.current?.parentElement;
    if (!popup?.isConnected) return undefined;
    return inertOutside(popup);
  }, []);
  return <span ref={marker} hidden />;
}
